import { NavLink } from '@festina-life/ui/actions'
import { Tooltip } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Table } from '@festina-life/ui/listsAndTables'
import { Icon, Typography } from '@festina-life/ui/structure'
import type { HierarchyPointNode } from '@visx/hierarchy'
import cx from 'classnames'
import { type MouseEventHandler, useMemo } from 'react'
import { useNavigate } from 'react-router'

import { useLayoutContext } from '../../context/layoutContext'
import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import { useTemplateIsLibrary } from '../../hooks/useTemplateIsLibrary'
import { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useWorkspaceManagerContext } from '../../workspace/workspaceManagerContext'
import { ConfigurationMarkdownViewer } from '../markdownviewer/ConfigurationMarkdownViewer'
import { TemplatePath } from '../path/TemplatePath'
import { TextRectangle } from '../shapes/TextRectangle'
import { TEXT_BOX_PADDING_X, TEXT_BOX_PADDING_Y } from './constants'
import { getNodeLinks } from './d3Helpers'
import type { TreeNode } from './types'

/* For Identifiable subtemplates we render an icon next to the text so the measuring text used by the TextRectangle
 * needs to include some placeholder character that will take up the space used by the identifiable subtenplate icon. */
const IDENTIFIABLE_PLACEHOLDER_CHARACTERS = '@'

type Props<T> = {
  /** D3 Node being rendered. */
  node: T
  onMouseOver?: MouseEventHandler<SVGRectElement>
  onMouseOut?: MouseEventHandler<SVGRectElement>
}

/** Renders a node from the tree given a D3 TreeNode. The component contains the required logic for measuring the node's
 * text and calculating the bounding rectangle's dimensions based on the text size.
 */
export const ModelTreeNode = <T extends HierarchyPointNode<TreeNode>>({
  node,
  onMouseOver,
  onMouseOut,
}: Props<T>) => {
  const { templateVersion } = useTemplateContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)
  const {
    preferences: {
      templates: { isShowingEmptyValues },
    },
  } = useLayoutContext()
  const translate = useTranslate()
  const { branch, server } = useNavigationParams()
  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const templateIsLibrary = useTemplateIsLibrary()
  const navigate = useNavigate()

  const top = node.x
  const left = node.y
  const matchingPaths = useMemo(
    () =>
      templateVersion
        ? getNodeLinks({
            currentTemplateVersion: templateVersion,
            forNode: node,
            metaModel,
            isShowingEmptyValues,
          }).map((path) => ({
            path,
            link: configurationItem({
              workspaceName: currentWorkspaceKey?.name,
              server,
              itemId: templateVersion.versionInfo.identifier,
              layer: templateVersion.layer,
              block: templateVersion.block,
              branch,
              type: templateIsLibrary(templateVersion.versionInfo.identifier)
                ? 'library'
                : 'template',
              path,
            }),
          }))
        : [],
    [
      templateVersion,
      node,
      metaModel,
      isShowingEmptyValues,
      currentWorkspaceKey?.name,
      server,
      branch,
      templateIsLibrary,
    ],
  )

  const handleNodeClicked = async () => {
    if (matchingPaths.length) {
      await navigate(matchingPaths[0].link)
    }
  }

  // oxlint-disable-next-line prefer-template
  const measuringText = `${node.data.isIdentifiableTemplate ? IDENTIFIABLE_PLACEHOLDER_CHARACTERS + ' ' : ''}${node.data.name}`

  return (
    <Tooltip
      isInteractive
      tip={
        <div className="flex flex-col gap-y-2 max-h-[50vh] overflow-y-auto">
          {!!matchingPaths.length && templateVersion && (
            <>
              <Typography weight="semibold">
                {translate(
                  'templateViewer.sidebar.modelDiagram.node.title.links',
                )}
              </Typography>
              <div>
                <p className="mb-2">
                  {translate('templateViewer.sidebar.modelDiagram.node.paths')}
                </p>
                <div className="flex flex-col gap-y-2">
                  {matchingPaths.map(({ path, link }) => (
                    <NavLink key={path} to={link} className="justify-start">
                      <TemplatePath
                        block={templateVersion.block}
                        layer={templateVersion.layer}
                        key={path}
                        value={path}
                        isInteractive={false}
                      />
                    </NavLink>
                  ))}
                </div>
              </div>
            </>
          )}
          <Typography weight="semibold">
            {translate(
              'templateViewer.sidebar.modelDiagram.node.title.documentation',
            )}
          </Typography>
          <Table className="border-collapse">
            <tbody>
              {node.data.documentation.map(({ property, documentation }) => (
                <tr
                  key={property}
                  className="border border-gray-100 dark:border-gray-600"
                >
                  <td className="px-2">
                    <code>{property}</code>
                  </td>
                  <td>
                    <ConfigurationMarkdownViewer shouldTransformLinksToDocSite>
                      {documentation}
                    </ConfigurationMarkdownViewer>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      }
    >
      {/* Festina tooltips requires an html element to be used as the tooltip trigger */}
      <g>
        <TextRectangle
          top={top}
          left={left}
          horizontalPadding={TEXT_BOX_PADDING_X}
          verticalPadding={TEXT_BOX_PADDING_Y}
          text={measuringText}
          rectClassName={cx('stroke-2', {
            'fill-modelTemplated': node.data.templated === 'templated',
            'fill-modelNonTemplated': node.data.templated === 'notTemplated',
            'fill-subtemplateCurrent': node.data.templated === 'otherTemplated',
            'fill-mudlr-stereotypeConfigurationOnly':
              node.data.templated === 'configurationOnly',
            'cursor-not-allowed': !matchingPaths.length,
          })}
          groupClassName="hover:opacity-90 cursor-pointer"
          onClick={handleNodeClicked}
          rounded="md"
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
        >
          {({ width, height }) => (
            <>
              {node.data.isIdentifiableTemplate && (
                <Icon
                  name="CheckBadge"
                  className="text-amaze-inkBright"
                  color="unset"
                  extraProps={{
                    x: 1,
                    y: 1,
                    width: 16,
                    height: 16,
                  }}
                />
              )}
              <text
                x={width / 2 + (node.data.isIdentifiableTemplate ? 6 : 0)}
                y={height / 2}
                dominantBaseline="middle"
                textAnchor="middle"
                className={cx('pointer-events-none fill-amaze-inkBright', {
                  italic: node.data.templated === 'configurationOnly',
                })}
              >
                {node.data.name}
              </text>
            </>
          )}
        </TextRectangle>
      </g>
    </Tooltip>
  )
}
